import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useSpareTransferReport } from "@/features/spares/reportsApi";

export const Route = createFileRoute("/_authenticated/reports/spare-transfer-register")({
  component: SpareTransferRegisterReport,
});

const STATUSES = ["Pending Approval", "Approved", "Completed", "Rejected", "Cancelled"];

function SpareTransferRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const { currentBranchId, branches } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const [fromBranch, setFromBranch] = useState<string>("all");
  const [toBranch, setToBranch] = useState<string>("all");

  const { data, isLoading } = useSpareTransferReport({
    branchId: effectiveBranchId,
    fromBranchId: fromBranch === "all" ? null : fromBranch,
    toBranchId: toBranch === "all" ? null : toBranch,
    status: status === "all" ? null : status,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    search: search || null,
  });
  const rows = data ?? [];

  const exportRows = useMemo(() => () => rows.map((r) => ({
    "Transfer No": r.transfer_no,
    "Spare Code": r.spare?.code ?? "",
    "Spare Name": r.spare?.name ?? "",
    Quantity: r.quantity,
    "From Branch": r.from_branch?.name ?? "",
    "From Department": r.from_department?.name ?? "",
    "From Store": r.from_store_location,
    "To Branch": r.to_branch?.name ?? "",
    "To Department": r.to_department?.name ?? "",
    "To Store": r.to_store_location,
    Status: r.status,
    "Requested By": r.requester?.full_name ?? r.requester?.email ?? "",
    "Requested At": r.requested_at,
    "Approved By": r.approver?.full_name ?? r.approver?.email ?? "",
    "Approved At": r.approved_at ?? "",
    "Completed At": r.completed_at ?? "",
    "Rejection Reason": r.rejection_reason ?? "",
    Remarks: r.remarks ?? "",
  })), [rows]);

  return (
    <ReportShell
      id="R-37" title="Spare Transfer Register"
      description="Spare transfer requests and completed transfers across branches and departments."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From Branch</Label>
            <Select value={fromBranch} onValueChange={setFromBranch}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To Branch</Label>
            <Select value={toBranch} onValueChange={setToBranch}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Transfer no / spare" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Transfer No</TableHead>
                <TableHead>Spare</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Approved</TableHead>
                <TableHead>Completed</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.transfer_no}</TableCell>
                    <TableCell><div className="font-medium">{r.spare?.name}</div><div className="font-mono text-xs text-muted-foreground">{r.spare?.code}</div></TableCell>
                    <TableCell className="text-right tabular-nums">{r.quantity}</TableCell>
                    <TableCell className="text-xs">{r.from_branch?.name ?? "—"}<br />{r.from_department?.name ?? ""} · {r.from_store_location}</TableCell>
                    <TableCell className="text-xs">{r.to_branch?.name ?? "—"}<br />{r.to_department?.name ?? ""} · {r.to_store_location}</TableCell>
                    <TableCell><Badge variant="secondary">{r.status}</Badge></TableCell>
                    <TableCell className="text-xs">{r.requested_at.slice(0, 10)}<br />{r.requester?.full_name ?? r.requester?.email ?? ""}</TableCell>
                    <TableCell className="text-xs">{r.approved_at?.slice(0, 10) ?? "—"}<br />{r.approver?.full_name ?? r.approver?.email ?? ""}</TableCell>
                    <TableCell className="text-xs">{r.completed_at?.slice(0, 10) ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} transfers.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
